import $ from "jquery";
import type { MapView } from "./Basemap";
import type {
    ForecastTime,
    InitialTime,
    ShadeSelector,
    model,
} from "./Controller";
import { Particle2D } from "./Particle2D";
import { ParticleGL } from "./ParticleGL";
import {
    PARTICLE_COLORS,
    PARTICLE_STEPS_PER_FT,
    type ParticleColorMode,
    type ParticleField,
    type ParticleGeometry,
    type ParticleRenderer,
    type ParticleSettings,
} from "./ParticleTypes";
import { resourcePath } from "./paths";
import { getMapMeta } from "./MapMeta";
import { setFeatureAvailability } from "./Form";

const NS = "http://www.w3.org/2000/svg";
const SUPPORTED_MODELS = new Set<model>(["GSM", "MSM", "QMA"]);
const GRID = {
    GSM: { dx: 20, dy: 20, xc: 10, yc: 10 },
    MSM: { dx: 10, dy: 10, xc: 0, yc: 0 },
    QMA: { dx: 10, dy: 10, xc: 0, yc: 0 },
} as const;

export class Particle {
    private enabled = false;
    private settings: ParticleSettings = {
        density: 5,
        speed: 2,
        age: 10,
        length: 6,
        lineWidth: 1,
        colorThreshold: 3,
        colorMode: "B",
    };
    private renderer: ParticleRenderer | null = null;
    private field: ParticleField | null = null;
    private geometry: ParticleGeometry | null = null;
    private abortController: AbortController | null = null;
    private requestGeneration = 0;
    private animationFrame = 0;
    private playing = false;
    private frameBusy = false;
    private lastFrameAt = 0;
    private legendX = 50;
    private legendY = 350;
    private legendType: "V" | "N" = "V";
    private glMode: "0" | "1" | null = null;
    private changeCallback: (task: Promise<void>) => void = () => {};
    private readonly canvas =
        document.querySelector<HTMLCanvasElement>("#particle_canvas")!;
    private readonly foreignObject =
        document.querySelector<SVGForeignObjectElement>(
            "#particle_foreign_object",
        )!;
    private readonly status =
        document.querySelector<HTMLElement>("#result_PC")!;
    private readonly loadButton =
        document.querySelector<HTMLInputElement>("#btn_PCload")!;
    private readonly playButton =
        document.querySelector<HTMLInputElement>("#btn_PCplay")!;
    private readonly stopButton =
        document.querySelector<HTMLInputElement>("#btn_PCstop")!;

    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
        private readonly initialTime: InitialTime,
        private readonly forecastTime: ForecastTime,
        private readonly mapView: MapView,
        private readonly renderFrame: (ft: number) => Promise<void>,
    ) {
        $("#btn_PC_on").on("click", () =>
            this.changeCallback(this.setEnabled(true)),
        );
        $("#btn_PC_off").on("click", () =>
            this.changeCallback(this.setEnabled(false)),
        );
        this.loadButton.addEventListener("click", () =>
            this.changeCallback(this.load()),
        );
        this.playButton.addEventListener("click", () => this.play());
        this.stopButton.addEventListener("click", () => {
            this.stop();
            this.setStatus("停止しました。");
        });
        for (const element of document.querySelectorAll<HTMLAnchorElement>(
            ".particle-preset",
        )) {
            element.addEventListener("click", (event) => {
                event.preventDefault();
                this.usePreset(element.dataset.preset || "normal");
            });
        }
        for (const selector of [
            "#input_PCden",
            "#input_PCspd",
            "#input_PCage",
            "#input_PClen",
            "#input_PClwd",
            "#input_PCcth",
            "#select_PCcols",
        ]) {
            document
                .querySelector(selector)
                ?.addEventListener("change", () => this.onSettingsChanged());
        }
        this.loadParams();
    }

    loadParams() {
        this.stop();
        this.enabled = (this.params.get("pc_el") || "") !== "";
        this.settings = {
            density: this.readNumber("pc_den", 5, 1, 100),
            speed: this.readNumber("pc_spd", 2, 0.1, 20),
            age: this.readNumber("pc_age", 10, 1, 100),
            length: this.readNumber("pc_len", 6, 1, 100),
            lineWidth: this.readNumber("pc_lwd", 1, 0.2, 20),
            colorThreshold: this.readNumber("pc_cth", 3, 2, 6),
            colorMode: this.readColorMode(this.params.get("pc_col")),
        };
        this.legendX = this.readNumber("lgdP_x", 50, -1000, 4000);
        this.legendY = this.readNumber("lgdP_y", 350, -1000, 3000);
        this.legendType = this.params.get("lgdP_type") === "N" ? "N" : "V";
        this.glMode =
            this.params.get("pc_gl") === "0" || this.params.get("pc_gl") === "1"
                ? (this.params.get("pc_gl") as "0" | "1")
                : null;
        this.updateForm();
        this.updateOnOff();
        this.drawLegend();
    }

    onChange(callback: (task: Promise<void>) => void) {
        this.changeCallback = callback;
    }

    saveParams() {
        this.params.set("pc_el", this.enabled ? "srf" : "");
        this.params.set("pc_den", String(this.settings.density));
        this.params.set("pc_spd", String(this.settings.speed));
        this.params.set("pc_age", String(this.settings.age));
        this.params.set("pc_len", String(this.settings.length));
        this.params.set("pc_lwd", String(this.settings.lineWidth));
        this.params.set("pc_cth", String(this.settings.colorThreshold));
        this.params.set("pc_col", this.settings.colorMode);
        this.params.set("lgdP_x", String(Math.round(this.legendX)));
        this.params.set("lgdP_y", String(Math.round(this.legendY)));
        this.params.set("lgdP_type", this.legendType);
        if (this.glMode !== null) {
            this.params.set("pc_gl", this.glMode);
        }
    }

    async onVariableChanged() {
        this.updateAvailability();
        if (!this.enabled) {
            return;
        }
        await this.load();
    }

    async onInitialTimeChanged() {
        if (!this.enabled) {
            return;
        }
        await this.load();
    }

    async onViewChanged() {
        if (!this.enabled) {
            return;
        }
        await this.load();
    }

    // 動画/ZIP 生成が FT を駆動する間は再生ループと競合するため停止させる
    // (renderTo は決定的なので、フレーム毎の onForecastTimeChanged 追従で正しい状態になる)
    stopPlayback() {
        if (this.playing) {
            this.stop();
        }
    }

    async onForecastTimeChanged() {
        if (!this.enabled) {
            return;
        }
        if (!this.field || !this.renderer) {
            await this.load();
            return;
        }
        if (this.playing) {
            return;
        }
        const ft = this.forecastTime.getParams().ft.toNumber();
        this.renderer.renderTo(ft);
    }

    async refresh() {
        if (this.enabled) {
            await this.load();
        }
    }

    dispose() {
        this.stop();
        this.abortController?.abort();
        this.renderer?.dispose();
    }

    private async setEnabled(enabled: boolean) {
        if (
            enabled &&
            !SUPPORTED_MODELS.has(this.shadeSelector.getParams().model)
        ) {
            this.setStatus("GSM・MSM・QMAで利用できます。", true);
            return;
        }
        this.enabled = enabled;
        this.updateOnOff();
        this.drawLegend();
        if (enabled) {
            await this.load();
        } else {
            this.clear();
            this.setStatus("↑データを読み込んで下さい。");
        }
    }

    private async load() {
        if (!this.enabled) {
            return;
        }
        const model = this.shadeSelector.getParams().model;
        if (!SUPPORTED_MODELS.has(model)) {
            this.enabled = false;
            this.updateOnOff();
            this.clear();
            this.setStatus("GSM・MSM・QMAで利用できます。", true);
            return;
        }
        const { ini } = this.initialTime.getParams();
        if (!(await this.initialTime.hasLatest(`${model}W`, ini))) {
            this.enabled = false;
            this.updateOnOff();
            this.clear();
            this.setStatus("この初期値の風粒子データは作成中です。", true);
            return;
        }
        this.stop();
        this.abortController?.abort();
        const generation = ++this.requestGeneration;
        const controller = new AbortController();
        this.abortController = controller;
        this.loadButton.disabled = true;
        this.playButton.disabled = true;
        this.setStatus("読み込み中です。");
        try {
            const request = this.createRequest(model as "GSM" | "MSM" | "QMA");
            const query = new URLSearchParams({
                el: model,
                x: String(request.x),
                w: String(request.width),
                y: String(request.y),
                h: String(request.height),
            });
            const windQuery = new URLSearchParams(query);
            windQuery.set("s", String(request.startFt));
            windQuery.set("t", String(request.timeCount));
            windQuery.set("iniJ", ini.format("YYYYMMDD-HH"));
            const [projectionResponse, windResponse] = await Promise.all([
                fetch(`${resourcePath("get_p2m.php")}?${query}`, {
                    signal: controller.signal,
                }),
                fetch(`${resourcePath("get_pgrd.php")}?${windQuery}`, {
                    signal: controller.signal,
                }),
            ]);
            if (!projectionResponse.ok || !windResponse.ok) {
                throw new Error(
                    `HTTP ${projectionResponse.status}/${windResponse.status}`,
                );
            }
            const [projectionBuffer, windBuffer] = await Promise.all([
                projectionResponse.arrayBuffer(),
                windResponse.arrayBuffer(),
            ]);
            if (generation !== this.requestGeneration) {
                return;
            }
            const gridN = request.width * request.height;
            const windN = gridN * request.timeCount;
            if (projectionBuffer.byteLength !== gridN) {
                throw new Error(
                    `投影係数サイズ不一致: ${projectionBuffer.byteLength}/${gridN}`,
                );
            }
            if (windBuffer.byteLength !== windN * 4) {
                throw new Error(
                    `風データサイズ不一致: ${windBuffer.byteLength}/${windN * 4}`,
                );
            }
            this.field = {
                u: new Int16Array(windBuffer, 0, windN),
                v: new Int16Array(windBuffer, windN * 2, windN),
                projection: new Uint8Array(projectionBuffer),
                width: request.width,
                height: request.height,
                timeCount: request.timeCount,
                startFt: request.startFt,
                endFt: request.endFt,
            };
            this.geometry = request.geometry;
            this.configureRenderer();
            this.foreignObject.style.display = "";
            this.drawLegend();
            if (
                document.querySelector<HTMLInputElement>("#cb_PCpreview")
                    ?.checked
            ) {
                this.renderer!.renderTo(request.startFt);
                this.play();
            } else {
                this.renderer!.renderTo(
                    this.forecastTime.getParams().ft.toNumber(),
                );
                this.setStatus(
                    `準備完了（${this.renderer!.kind === "webgl" ? "WebGL2" : "Canvas 2D"}）。`,
                );
            }
            this.playButton.disabled = false;
        } catch (error) {
            if ((error as DOMException).name !== "AbortError") {
                console.error(error);
                this.setStatus(
                    `読み込みに失敗しました: ${error instanceof Error ? error.message : String(error)}`,
                    true,
                );
            }
        } finally {
            if (generation === this.requestGeneration) {
                this.loadButton.disabled = false;
                this.abortController = null;
            }
        }
    }

    private configureRenderer() {
        if (!this.field || !this.geometry) {
            return;
        }
        this.renderer?.dispose();
        this.renderer = this.createRenderer();
        try {
            this.renderer.configure(this.field, this.geometry, this.settings);
        } catch (error) {
            if (this.renderer.kind !== "webgl") {
                throw error;
            }
            console.warn(
                "WebGL風粒子の初期化に失敗したためCanvas 2Dへ切り替えます。",
                error,
            );
            this.renderer.dispose();
            this.renderer = new Particle2D(this.canvas);
            this.renderer.configure(this.field, this.geometry, this.settings);
        }
        if (this.renderer.kind === "2d") {
            this.foreignObject.setAttribute("filter", "url(#f_alpha)");
        } else {
            this.foreignObject.removeAttribute("filter");
        }
    }

    private createRenderer(): ParticleRenderer {
        if (this.glMode === "0") {
            return new Particle2D(this.canvas);
        }
        try {
            return new ParticleGL(
                this.canvas,
                () => this.fallbackTo2D(),
                this.glMode === "1",
            );
        } catch (error) {
            console.info("Canvas 2D風粒子を使用します。", error);
            return new Particle2D(this.canvas);
        }
    }

    private fallbackTo2D() {
        if (!this.field || !this.geometry) {
            return;
        }
        this.stop();
        this.renderer = new Particle2D(this.canvas);
        this.renderer.configure(this.field, this.geometry, this.settings);
        this.foreignObject.setAttribute("filter", "url(#f_alpha)");
        this.renderer.renderTo(this.forecastTime.getParams().ft.toNumber());
        this.setStatus("WebGLが失われたためCanvas 2Dへ切り替えました。", true);
    }

    private createRequest(model: "GSM" | "MSM" | "QMA") {
        const grid = GRID[model];
        const view = this.mapView.getViewBox();
        const bounds = this.rotatedBounds(view);
        const px0 =
            Math.floor((bounds.x0 - grid.xc) / grid.dx) * grid.dx + grid.xc;
        const px1 =
            Math.floor((bounds.x1 - grid.xc) / grid.dx + 1) * grid.dx + grid.xc;
        const py0 =
            Math.floor((bounds.y0 - grid.yc) / grid.dy) * grid.dy + grid.yc;
        const py1 =
            Math.floor((bounds.y1 - grid.yc) / grid.dy + 1) * grid.dy + grid.yc;
        const scale = 1920 / view.width;
        const rw = Math.ceil((px1 - px0) * scale);
        const rh = Math.ceil((py1 - py0) * scale);
        const meta = getMapMeta(model);
        const x = (px0 - meta.x0) / grid.dx;
        const y = (py0 - meta.y0) / grid.dy;
        const width = (px1 - meta.x0) / grid.dx - x + 1;
        const height = (py1 - meta.y0) / grid.dy - y + 1;
        const { minimum, maximum } = this.forecastTime.getRangeParams();
        const startFt = Math.ceil(minimum.toNumber());
        const endFt = Math.floor(maximum.toNumber());
        return {
            x,
            y,
            width,
            height,
            startFt,
            endFt,
            timeCount: endFt - startFt + 1,
            geometry: {
                gridDx: grid.dx,
                gridDy: grid.dy,
                scale,
                rx: view.deg === 0 ? (px0 - view.x) * scale : 0,
                ry: view.deg === 0 ? (py0 - view.y) * scale : 0,
                rw,
                rh,
                rx0: (px0 - view.x) * scale,
                ry0: (py0 - view.y) * scale,
                degree: view.deg,
                backWidth: view.deg === 0 ? 1920 : Math.max(1920, rw),
                backHeight: view.deg === 0 ? 1080 : Math.max(1080, rh),
            },
        };
    }

    private rotatedBounds(view: ReturnType<MapView["getViewBox"]>) {
        if (view.deg === 0) {
            return {
                x0: view.x,
                x1: view.x + view.width,
                y0: view.y,
                y1: view.y + view.height,
            };
        }
        const radians = (view.deg / 180) * Math.PI;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const cx = view.x + view.width / 2;
        const cy = view.y + view.height / 2;
        const points = [
            [view.x, view.y],
            [view.x + view.width, view.y],
            [view.x, view.y + view.height],
            [view.x + view.width, view.y + view.height],
        ].map(([x, y]) => ({
            x: (x - cx) * cos - (y - cy) * sin + cx,
            y: (x - cx) * sin + (y - cy) * cos + cy,
        }));
        return {
            x0: Math.min(...points.map((point) => point.x)),
            x1: Math.max(...points.map((point) => point.x)),
            y0: Math.min(...points.map((point) => point.y)),
            y1: Math.max(...points.map((point) => point.y)),
        };
    }

    private play() {
        if (!this.renderer || !this.field || this.playing) {
            return;
        }
        this.playing = true;
        this.playButton.disabled = true;
        this.stopButton.disabled = false;
        this.lastFrameAt = performance.now();
        this.setStatus(
            `再生中（${this.renderer.kind === "webgl" ? "WebGL2" : "Canvas 2D"}）。`,
        );
        this.animationFrame = requestAnimationFrame((time) => this.tick(time));
    }

    private tick(time: number) {
        if (!this.playing || !this.renderer || !this.field) {
            return;
        }
        const elapsed = time - this.lastFrameAt;
        const steps = Math.min(4, Math.floor(elapsed / (1000 / 30)));
        if (steps > 0 && !this.frameBusy) {
            this.lastFrameAt += steps * (1000 / 30);
            for (let index = 0; index < steps; index++) {
                const ft = this.renderer.step();
                if (ft >= this.field.endFt) {
                    this.renderer.present();
                    this.stop();
                    this.setStatus("再生完了。");
                    return;
                }
                const tenth = Math.round(ft * PARTICLE_STEPS_PER_FT);
                if (tenth % PARTICLE_STEPS_PER_FT === 0) {
                    this.frameBusy = true;
                    void this.renderFrame(ft).finally(() => {
                        this.frameBusy = false;
                    });
                }
            }
            this.renderer.present();
        }
        this.animationFrame = requestAnimationFrame((next) => this.tick(next));
    }

    private stop() {
        this.playing = false;
        this.frameBusy = false;
        cancelAnimationFrame(this.animationFrame);
        this.playButton.disabled = !this.renderer;
        this.stopButton.disabled = true;
    }

    private onSettingsChanged() {
        this.settings = this.readSettingsFromForm();
        this.drawLegend();
        if (this.field && this.geometry && this.renderer) {
            this.stop();
            this.renderer.configure(this.field, this.geometry, this.settings);
            this.renderer.renderTo(this.forecastTime.getParams().ft.toNumber());
            this.setStatus("設定を反映しました。");
        }
    }

    private usePreset(name: string) {
        const presets: Record<string, Partial<ParticleSettings>> = {
            normal: { density: 5, speed: 2, age: 10, length: 6, lineWidth: 1 },
            real: { density: 5, speed: 1, age: 10, length: 8, lineWidth: 1.6 },
            short: { density: 10, speed: 1.5, age: 3, length: 3, lineWidth: 2 },
            long: { density: 2, speed: 1.5, age: 10, length: 10, lineWidth: 1 },
            thick: { density: 3, speed: 1.5, age: 7, length: 7, lineWidth: 5 },
        };
        Object.assign(this.settings, presets[name] || presets.normal);
        this.updateForm();
        this.onSettingsChanged();
    }

    private readSettingsFromForm(): ParticleSettings {
        const value = (selector: string, fallback: number) => {
            const parsed = Number(
                document.querySelector<HTMLInputElement>(selector)?.value,
            );
            return Number.isFinite(parsed) ? parsed : fallback;
        };
        return {
            density: Math.max(1, value("#input_PCden", 5)),
            speed: Math.max(0.1, value("#input_PCspd", 2)),
            age: Math.max(1, value("#input_PCage", 10)),
            length: Math.max(1, value("#input_PClen", 6)),
            lineWidth: Math.max(0.2, value("#input_PClwd", 1)),
            colorThreshold: Math.min(6, Math.max(2, value("#input_PCcth", 3))),
            colorMode: this.readColorMode(
                document.querySelector<HTMLSelectElement>("#select_PCcols")
                    ?.value,
            ),
        };
    }

    private updateForm() {
        const values: Record<string, number | string> = {
            "#input_PCden": this.settings.density,
            "#input_PCspd": this.settings.speed,
            "#input_PCage": this.settings.age,
            "#input_PClen": this.settings.length,
            "#input_PClwd": this.settings.lineWidth,
            "#input_PCcth": this.settings.colorThreshold,
            "#select_PCcols": this.settings.colorMode,
        };
        for (const [selector, value] of Object.entries(values)) {
            const element = document.querySelector<
                HTMLInputElement | HTMLSelectElement
            >(selector);
            if (element) {
                element.value = String(value);
            }
        }
    }

    private updateOnOff() {
        this.updateAvailability();
        $("#btn_PC_on")
            .toggleClass("btn_onA", this.enabled)
            .toggleClass("btn_onB", !this.enabled);
        $("#btn_PC_off")
            .toggleClass("btn_offB", this.enabled)
            .toggleClass("btn_offA", !this.enabled);
        $("#sdm_PC_detail").css("display", this.enabled ? "block" : "none");
    }

    private updateAvailability() {
        setFeatureAvailability(
            "PC",
            SUPPORTED_MODELS.has(this.shadeSelector.getParams().model),
        );
    }

    private clear() {
        this.stop();
        this.abortController?.abort();
        this.requestGeneration++;
        this.renderer?.dispose();
        this.renderer = null;
        this.field = null;
        this.geometry = null;
        this.foreignObject.style.display = "none";
        document.querySelector("#legendP")?.remove();
    }

    private drawLegend() {
        document.querySelector("#legendP")?.remove();
        if (!this.enabled || this.legendType === "N") {
            return;
        }
        const overlay = document.querySelector<SVGSVGElement>("#overlay")!;
        const group = document.createElementNS(NS, "g");
        group.id = "legendP";
        group.setAttribute(
            "transform",
            `translate(${this.legendX} ${this.legendY}) rotate(0.005)`,
        );
        group.style.cursor = "move";
        const rect = (
            x: number,
            y: number,
            width: number,
            height: number,
            fill: string,
            rx = 0,
        ) => {
            const node = document.createElementNS(NS, "rect");
            node.setAttribute("x", String(x));
            node.setAttribute("y", String(y));
            node.setAttribute("width", String(width));
            node.setAttribute("height", String(height));
            node.setAttribute("fill", fill);
            if (rx > 0) {
                node.setAttribute("rx", String(rx));
            }
            group.appendChild(node);
        };
        const text = (
            x: number,
            y: number,
            value: string,
            size: number,
            anchor = "middle",
        ) => {
            const node = document.createElementNS(NS, "text");
            node.setAttribute("x", String(x));
            node.setAttribute("y", String(y));
            node.setAttribute("font-size", String(size));
            node.setAttribute("text-anchor", anchor);
            node.setAttribute("fill", "#000");
            node.textContent = value;
            group.appendChild(node);
        };
        // 旧版 PC.legendP と同じレイアウト (m=13, th=60, セル50px×7, 単位余白0.3行)
        rect(0, 0, 150, 451, "#efe", 12);
        text(75, 55.5, "風速", 50);
        const colors = PARTICLE_COLORS[this.settings.colorMode];
        for (let level = 6; level >= 0; level--) {
            const row = 6 - level;
            rect(16.9, 73 + row * 50, 50, 50, `rgb(${colors[level].join(",")})`);
            if (level > 0) {
                text(
                    106.15,
                    73 + (row + 1) * 50 + 12.25,
                    String(level * this.settings.colorThreshold),
                    35,
                );
            }
        }
        text(137, 435.5, "m/s", 30, "end");
        this.enableLegendDrag(group, overlay);
        overlay.appendChild(group);
    }

    private enableLegendDrag(group: SVGGElement, overlay: SVGSVGElement) {
        let offsetX = 0;
        let offsetY = 0;
        const point = (event: PointerEvent) => {
            const svgPoint = overlay.createSVGPoint();
            svgPoint.x = event.clientX;
            svgPoint.y = event.clientY;
            return svgPoint.matrixTransform(overlay.getScreenCTM()!.inverse());
        };
        group.addEventListener("pointerdown", (event) => {
            const p = point(event);
            offsetX = p.x - this.legendX;
            offsetY = p.y - this.legendY;
            group.setPointerCapture(event.pointerId);
        });
        group.addEventListener("pointermove", (event) => {
            if (!group.hasPointerCapture(event.pointerId)) {
                return;
            }
            const p = point(event);
            this.legendX = p.x - offsetX;
            this.legendY = p.y - offsetY;
            group.setAttribute(
                "transform",
                `translate(${this.legendX} ${this.legendY}) rotate(0.005)`,
            );
        });
    }

    private setStatus(message: string, error = false) {
        this.status.textContent = message;
        this.status.style.color = error ? "#c00" : "#009";
    }

    private readNumber(
        key: string,
        fallback: number,
        minimum: number,
        maximum: number,
    ) {
        if (!this.params.has(key)) {
            return fallback;
        }
        const value = Number(this.params.get(key));
        return Number.isFinite(value) && value >= minimum && value <= maximum
            ? value
            : fallback;
    }

    private readColorMode(value: string | null | undefined): ParticleColorMode {
        return value === "A" || value === "W" ? value : "B";
    }
}
